import { type PieceButton, type PieceComponent, type PieceLogo, GarmentPieceFabrics } from '@seddi/scene';
import { Sphere, Vector3 } from '@seddi/three';

import { ArrayDiff } from '../core/reconciler/arrayDiff';
import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  type PieceButtonRenderable,
  create as createButton,
  dispose as disposeButton,
} from '../renderables/pieceButtonRenderable';
import {
  type PieceLogoRenderable,
  create as createLogo,
  dispose as disposeLogo,
} from '../renderables/pieceLogoRenderable';
import {
  type PieceRenderable,
  attachFabric,
  attachGarmentGeometry,
  detachFabric,
  detachGarmentGeometry,
  recreateGroups,
  recreateMaterials,
  updateGeometry,
  updateGrain,
} from '../renderables/pieceRenderable';
import { getFabricColorId } from '../resources/fabric';
import type { FabricWithColor } from '../resources/fabricTypes';
import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import { update1st as updateButton1st, update2nd as updateButton2nd } from './pieceButtonReconciler';
import { update as updateLogo } from './pieceLogoReconciler';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';
import { PieceGeometryDesc } from '../scene/sceneTypes';

const InfinitySphere = new Sphere(new Vector3(), Infinity);

/**
 * Reconcile
 * @param renderable - Renderable
 * @param nextState - Piece next.
 * @param prevState - Piece prev.
 * @param frame - Frame.
 */
export const update1st = (
  renderable: PieceRenderable,
  nextState: PieceComponent,
  prevState: PieceComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const {
    nextGarmentGeometry,
    nextUseGarmentGeometry,
    prevGarmentGeometry,
    prevUseGarmentGeometry,
    resources,
  } = frame;

  let dirty = false;
  let isGeometryDirty = false;

  // Update from garment geometry.
  if (nextGarmentGeometry !== prevGarmentGeometry) {
    if (prevGarmentGeometry) {
      detachGarmentGeometry(renderable, resources);
    }
    if (nextGarmentGeometry) {
      attachGarmentGeometry(renderable, nextState, nextGarmentGeometry, resources);
    }
    dirty = true;
    isGeometryDirty = true;
    frame.entities.get(renderable.id)!.isGeometryDirty = true;
  }

  const diffProps = PropsDiff.diff(nextState, prevState);

  const isGarmentGeometryDirty = !nextUseGarmentGeometry && prevUseGarmentGeometry;
  if ((!nextUseGarmentGeometry && diffProps.includes('geometry')) || isGarmentGeometryDirty) {
    const nextGeometry = nextState.geometry as PieceGeometryDesc;
    const prevGeometry = isGarmentGeometryDirty
      ? undefined
      : (prevState?.geometry as PieceGeometryDesc | undefined);
    const diffGeometry = {
      indices: nextGeometry.indices !== prevGeometry?.indices && nextGeometry.indices,
      position2D: nextGeometry.position2D !== prevGeometry?.position2D && nextGeometry.position2D,
      position3D: nextGeometry.position3D !== prevGeometry?.position3D && nextGeometry.position3D,
      normal: nextGeometry.normal !== prevGeometry?.normal && nextGeometry.normal,
    };
    updateGeometry(renderable, diffGeometry);
    dirty = true;
    isGeometryDirty = true;
    frame.entities.get(renderable.id)!.isGeometryDirty = true;
  }
  // Fabric affects materials.
  if (diffProps.includes('fabric') || diffProps.includes('fabricColor')) {
    dirty = updateFabric(renderable, nextState, prevState, frame) || dirty;
  }
  // Grain affects materials.
  if (diffProps.includes('grain') && renderable.fabricRef) {
    dirty = updateGrain(renderable, nextState.grain) || dirty;
  }
  // Logos affects materials.
  if (diffProps.includes('logos')) {
    dirty = updateLogos(renderable, nextState.logos, prevState?.logos, frame) || dirty;
  }
  // Buttons placement depends on piece geometry.
  if (diffProps.includes('buttons') || isGeometryDirty) {
    dirty = updateButtons1st(renderable, nextState.buttons, prevState?.buttons, frame) || dirty;
  }

  return dirty;
};

/**
 * Reconcile
 * @param renderable - Renderable
 * @param nextState - Piece next.
 * @param prevState - Piece prev.
 * @param frame - Frame.
 */
export const update2nd = (
  renderable: PieceRenderable,
  nextState: PieceComponent,
  prevState: PieceComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  let dirty = false;

  // Buttons placement depends on hole parents being updated first.
  dirty = updateButtons2nd(renderable, nextState.buttons, frame) || dirty;

  // dirty =
  //   ButtonGfxsLinesReconciler.Update(
  //     scene,
  //     nextScene,
  //     prevScene,
  //     nextShowGarmentGeometry,
  //     this,
  //   ) || dirty;

  return dirty;
};

/**
 * Update fabric.
 * @param renderable - Piece renderable.
 * @param nextState - Piece component prev.
 * @param prevState - Piece component next.
 * @param frame - Frame.
 */
const updateFabric = (
  renderable: PieceRenderable,
  nextState: PieceComponent | undefined,
  prevState: PieceComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources, nextScene } = frame;

  if (prevState) {
    detachFabric(renderable, resources);
  }
  if (!nextState) {
    return true;
  }

  const fabricColorId = getFabricColorId(nextState.fabric, nextState.fabricColor);
  let dirty = false;

  if (resources.findFabric(fabricColorId)) {
    attachFabric(renderable, fabricColorId, nextState.grain, frame.resources);
    dirty = true;
  } else {
    if (!resources.hasFabricRequest(fabricColorId)) {
      const fabric = GarmentPieceFabrics.FindFabricInScene(nextScene, nextState.fabric);
      const fabricWithColor: FabricWithColor = {
        ...fabric,
        id: fabricColorId,
        recolorTarget: nextState.fabricColor,
      };
      resources.loadFabricDeferred(fabricWithColor);
    }
  }

  return dirty;
};

/**
 * Update buttons.
 * @param renderable - Piece renderable.
 * @param nextState - Buttons next.
 * @param prevState - Buttons prev.
 * @param frame - Frame.
 */
const updateButtons1st = (
  renderable: PieceRenderable,
  nextState: PieceButton[],
  prevState: PieceButton[] | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { config, resources } = frame;
  const { frustumCulling } = config;

  const [nextDiff, prevDiff] = ArrayDiff.diff(nextState ?? [], prevState ?? []);

  const { buttons } = renderable;
  const map = buttons.reduce<Map<string, PieceButtonRenderable>>((map, button) => {
    map.set(button.id, button);
    return map;
  }, new Map());

  buttons.length = 0;

  for (const pair of nextDiff) {
    let button = map.get(pair.next._id);
    if (!button) {
      button = createButton(pair.next._id, renderable.mesh.parent);
      if (!frustumCulling) {
        button.mesh.frustumCulled = config.frustumCulling;
        button.mesh.geometry.boundingSphere = InfinitySphere;
      }
    }
    updateButton1st(button, pair.next, pair.prev, renderable, frame);
    buttons.push(button);
  }

  for (const prev of prevDiff) {
    const button = map.get(prev._id);
    disposeButton(button, resources);
  }

  return true;
};

/**
 * Update buttons.
 * @param renderable - Piece renderable.
 * @param props - Buttons next.
 * @param frame - Frame.
 */
const updateButtons2nd = (
  renderable: PieceRenderable,
  props: PieceButton[],
  frame: SceneReconcilerFrame,
): boolean => {
  const bs = renderable.buttons;
  const map = bs.reduce<Map<string, PieceButtonRenderable>>((map, b) => {
    map.set(b.id, b);
    return map;
  }, new Map());

  for (const button of props) {
    const buttonRenderable = map.get(button._id)!;
    updateButton2nd(buttonRenderable, button, renderable, frame);
  }

  return true;
};

/**
 * Update logos.
 * @param renderable - Piece renderable.
 * @param nextState - Logos next.
 * @param prevState - Logos prev.
 * @param frame - Frame.
 */
const updateLogos = (
  renderable: PieceRenderable,
  nextState: PieceLogo[],
  prevState: PieceLogo[] | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources } = frame;

  const [nextDiff, prevDiff] = ArrayDiff.diff(nextState ?? [], prevState ?? []);

  const ls = renderable.logos;
  const map = ls.reduce<Map<string, PieceLogoRenderable>>((map, l) => {
    map.set(l.id, l);
    return map;
  }, new Map());

  ls.length = 0;

  for (const pair of nextDiff) {
    const l = map.get(pair.next._id) ?? createLogo(pair.next._id);
    updateLogo(l, pair.next, pair.prev, frame);
    ls.push(l);
  }

  for (const prev of prevDiff) {
    const l = map.get(prev._id);
    disposeLogo(l, resources);
  }

  recreateMaterials(renderable);
  recreateGroups(renderable);

  return true;
};
